// src/components/job-engine/useJob.ts
import { useCallback, useEffect, useRef, useState } from "react";
import { isAxiosError } from "axios";
import { getJob } from "@/api/jobEngine";
import type { Job } from "@/api/jobEngine";

export interface UseJobResult {
  data: Job | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useJob(jobId: string): UseJobResult {
  const [data, setData] = useState<Job | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const abortRef = useRef<AbortController | null>(null);
  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!jobId) return;
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setIsLoading(true);
    setError(null);
    getJob(jobId, ctrl.signal)
      .then((r) => { if (!ctrl.signal.aborted) setData(r); })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (isAxiosError(err)) setError(err.response?.data?.title ?? "Failed to load job");
        else setError("An unexpected error occurred");
      })
      .finally(() => { if (!ctrl.signal.aborted) setIsLoading(false); });
    return () => ctrl.abort();
  }, [jobId, tick]);

  return { data, isLoading, error, refetch };
}
